/**
 * Cyber threat layer renderer.
 * cyber-threats: ScatterplotLayer colored by severity (Feodo/URLhaus/OTX indicators).
 * (GPS jamming moved to h3-jamming-layer.ts — WM renders it as an H3 hex grid.)
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const CYBER_LAYER_IDS = new Set(['cyber-threats']);

/**
 * Cyber threat severity colors — WM DeckGLMap.ts:3511-3518. Alpha is part
 * of the encoding: higher severity is both bigger AND more opaque. WM's
 * default branch (yellow 170) covers everything below medium.
 */
export const CYBER_SEVERITY_COLOR: Record<Severity, [number, number, number, number]> = {
  critical: [255, 61, 0, 225],
  high: [255, 102, 0, 205],
  medium: [255, 176, 0, 185],
  low: [255, 235, 59, 170],
  info: [255, 235, 59, 170],
};

/** Create layers for cyber threat indicators. */
function createCyberThreatLayer(
  events: NormalizedEvent[],
  _zoom: number,
): Layer {
  return new ScatterplotLayer<NormalizedEvent>({
    id: 'cyber-threats-dots',
    data: events,
    pickable: true,
    filled: true,
    stroked: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => CYBER_SEVERITY_COLOR[d.severity ?? 'info'],
    getLineColor: [255, 255, 255, 160],
    lineWidthMinPixels: 1,
    getRadius: (d) => {
      switch (d.severity) {
        case 'critical': return 22000;
        case 'high': return 17000;
        case 'medium': return 13000;
        default: return 9000;
      }
    },
    radiusMinPixels: 6,
    radiusMaxPixels: 18,
    // No layer opacity — WM bakes the transparency into the per-severity alpha
    updateTriggers: {
      getFillColor: [events.length],
      getRadius: [events.length],
    },
  });
}

/** Create deck.gl layers for cyber threat data. */
export function createCyberLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  return [createCyberThreatLayer(events, zoom)];
}
